// GA4 product-funnel analytics for the DIY Plan Measure / Floor Plan Creator
// workflow. Reuses the gtag.js installation already loaded by
// public/index.html (and optionally re-configured by SEO.jsx) — no new
// analytics platform is introduced here.
//
// Every call is fire-and-forget and NEVER throws into app logic: if GA4 is
// blocked, absent, or not yet loaded, tracking is a silent no-op and the
// underlying user action (drawing, saving, exporting, paying) proceeds
// exactly as before.
//
// Privacy: only project.id (a random client-generated UUID) and small
// integer counts / enums are ever sent. No names, addresses, geometry, room
// names, or file contents.
import { PROJECT_UNLOCK_PRICE_GBP } from "@/lib/entitlement";

const FIRED_KEY_PREFIX = "hpd_ga4_once:";
const firedMemory = new Set();

function hasFired(key) {
  if (firedMemory.has(key)) return true;
  try {
    if (sessionStorage.getItem(FIRED_KEY_PREFIX + key) === "1") {
      firedMemory.add(key);
      return true;
    }
  } catch {
    // sessionStorage unavailable (private mode, blocked) — memory-only
    // dedupe for the remainder of this tab's life is an acceptable fallback.
  }
  return false;
}

function markFired(key) {
  firedMemory.add(key);
  try {
    sessionStorage.setItem(FIRED_KEY_PREFIX + key, "1");
  } catch {
    // noop
  }
}

// GA4 rejects/mangles undefined params — strip them before sending.
function cleanParams(params) {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) out[k] = v;
  });
  return out;
}

/**
 * Sends a single GA4 event through the existing gtag() global. Safe to call
 * unconditionally — no-ops if gtag isn't available.
 */
export function trackEvent(eventName, params = {}) {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", eventName, cleanParams(params));
  } catch {
    // Analytics must never break the app it's observing.
  }
}

/**
 * Like trackEvent, but only ever fires once per (eventName, projectId) for
 * the current browser tab session — used for one-time funnel milestones
 * (plan_started, first_wall_drawn, project_saved, unlock_started,
 * unlock_completed) so re-renders, autosave loops, or component remounts
 * never produce duplicate events for the same project.
 */
export function trackOnce(eventName, projectId, params = {}) {
  if (!projectId) return;
  const key = `${eventName}:${projectId}`;
  if (hasFired(key)) return;
  markFired(key);
  trackEvent(eventName, params);
}

// Coarse, PII-free device bucket for plan_started.
export function getDeviceType() {
  try {
    const ua = window.navigator?.userAgent || "";
    if (/iPad|Tablet|(Android(?!.*Mobile))/i.test(ua)) return "tablet";
    if (/Mobi|iPhone|Android/i.test(ua)) return "mobile";
    return "desktop";
  } catch {
    return "unknown";
  }
}

const TOOL = "plan_measure";

// Fires once per project: the first genuine drawing/editing action in
// either the Plan Measure or Floor Plan Creator workspace (not just the
// page loading).
export function trackPlanStarted(projectId, { unlocked } = {}) {
  trackOnce("plan_started", projectId, {
    project_id: projectId,
    tool: TOOL,
    device_type: getDeviceType(),
    project_status: unlocked ? "unlocked" : "draft",
  });
}

// Fires once per project, only for a wall that was actually committed to the
// floor plan (never for in-progress drags or cancelled gestures — callers
// only invoke this after a successful create/commit).
export function trackFirstWallDrawn(projectId, wallCount) {
  trackOnce("first_wall_drawn", projectId, {
    project_id: projectId,
    tool: TOOL,
    wall_count: wallCount,
  });
}

// Fires once per project, when the app first recognises/creates a valid
// enclosed room (manually drawn, auto-detected on wall-closure, "Detect
// rooms", or auto-trace).
export function trackFirstRoomCreated(projectId, roomCount) {
  trackOnce("first_room_created", projectId, {
    project_id: projectId,
    tool: TOOL,
    room_count: roomCount,
  });
}

// Fires once per project — the first time the project is genuinely
// persisted (successful autosave), deliberately NOT on every autosave tick.
export function trackProjectSaved(projectId, signals = {}) {
  trackOnce("project_saved", projectId, {
    project_id: projectId,
    tool: TOOL,
    wall_count: signals.wall_count,
    room_count: signals.room_count,
    floor_count: signals.floor_count,
  });
}

// Fires on every export attempt, BEFORE the paywall/unlock decision, so
// export intent from both locked and unlocked users is captured.
export function trackExportClicked(projectId, exportType, isUnlocked, signals = {}) {
  trackEvent("export_clicked", {
    project_id: projectId,
    export_type: exportType,
    is_unlocked: !!isUnlocked,
    wall_count: signals.wall_count,
    room_count: signals.room_count,
    floor_count: signals.floor_count,
  });
}

// Fires once per project — intentional entry into the £2.99 unlock/payment
// funnel (the Unlock dialog opening in its payment view).
export function trackUnlockStarted(projectId) {
  trackOnce("unlock_started", projectId, {
    project_id: projectId,
    price: PROJECT_UNLOCK_PRICE_GBP,
    currency_code: "GBP",
    unlock_type: "project",
  });
}

// Fires each time the user actually clicks into the PayPal checkout
// (createOrder), never merely because the PayPal buttons rendered.
export function trackPaypalStarted(projectId) {
  trackEvent("paypal_started", {
    project_id: projectId,
    price: PROJECT_UNLOCK_PRICE_GBP,
    currency_code: "GBP",
    unlock_type: "project",
  });
}

// Fires once per project — the Layout/Present workspace was opened (tab
// switched to "layout"), regardless of whether anything is added to it yet.
export function trackLayoutOpened(projectId) {
  trackOnce("layout_opened", projectId, {
    project_id: projectId,
    tool: TOOL,
  });
}

// Fires once per project, ONLY after the backend has confirmed the unlock
// (never from a client-side-optimistic PayPal capture alone). This is the
// custom funnel event (kept distinct from GA4's standard "purchase" event
// below) — mark this as a GA4 key event/conversion in the GA4 admin UI.
export function trackUnlockCompleted(projectId, paypalOrderId, signals = {}) {
  trackOnce("project_unlock_completed", projectId, {
    project_id: projectId,
    unlock_type: "project",
    value: PROJECT_UNLOCK_PRICE_GBP,
    currency: "GBP",
    paypal_order_id: paypalOrderId,
    wall_count: signals.wall_count,
    room_count: signals.room_count,
    floor_count: signals.floor_count,
  });
}

// Fires GA4's standard e-commerce "purchase" event, ONCE per project, ONLY
// after the backend has confirmed a real, successful PayPal payment for the
// £2.99 project unlock. This powers GA4's built-in revenue/e-commerce
// reports; trackUnlockCompleted above remains the custom funnel event.
// Must NEVER fire for: checkout opening, PayPal starting, a failed payment,
// a complimentary/tester invite redemption, or an admin unlock/export — all
// of those skip this function entirely (see UnlockDialog.jsx / redeemInvite,
// which never calls this). One £2.99 payment => exactly one "purchase" call
// with that payment's own confirmed PayPal order/capture id as transaction_id,
// so re-tries of the same confirmed order never double-count revenue.
export function trackPurchase(projectId, paypalOrderId) {
  if (!paypalOrderId) return;
  trackOnce("purchase", paypalOrderId, {
    transaction_id: paypalOrderId,
    value: PROJECT_UNLOCK_PRICE_GBP,
    currency: "GBP",
    items: [{ item_id: "project_unlock", item_name: "Project unlock", price: PROJECT_UNLOCK_PRICE_GBP, quantity: 1 }],
  });
}

// Non-conversion engagement event for the Feb-2026 conversion pass — fired
// whenever a marketing/content-page CTA sends a visitor into HPD Draw (not
// a funnel milestone, so plain trackEvent — may legitimately fire more than
// once per session, e.g. a visitor clicking several CTAs while browsing).
// Never used for payment/unlock events — those keep their own dedicated
// trackers above.
export function trackHpdDrawCtaClicked(ctaLocation, ctaLabel, sourcePage, entryMode = "general") {
  trackEvent("hpd_draw_cta_clicked", {
    cta_location: ctaLocation,
    cta_label: ctaLabel,
    source_page: sourcePage,
    entry_mode: entryMode,
  });
}
